import { FormattedMessage, useIntl } from 'react-intl';
import { makeStyles, useTheme, Theme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Dialog, DialogTitle, IconButton, List, ListItem, ListItemText, ListItemIcon } from '@material-ui/core';
import {
  Close as CloseIcon,
  Computer as ComputerIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from '@material-ui/icons';

import { Shipment } from 'services/index.d';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginTop: 15,
    textAlign: 'center',
  },
  paper: {
    minWidth: 320,
  },
  link: {
    color: theme.palette.secondary.main,
    cursor: 'pointer',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  listItem: {
    '& .MuiListItemIcon-root': {
      minWidth: 32,
      marginBottom: 4,
      '& .MuiSvgIcon-root': {
        width: '0.6em',
        margin: '-10px -10px 0 0',
        fill: 'rgb(197, 205, 212)',
      },
    },

    '& .MuiListItemText-secondary': {
      fontWeight: 'bold',
    },

    '& .MuiListItemIcon-alignItemsFlexStart': {
      marginTop: 14,
    },

    '&:hover': {
      backgroundColor: theme?.palette?.sky?.main,
    },
  },
}));

export interface ContactInfoDialogProps {
  shipment: Shipment;
  open: boolean;
  onClose: () => void;
}

export default function ContactInfoDialog({ shipment, open, onClose }: ContactInfoDialogProps) {
  const { formatMessage } = useIntl();
  const classes = useStyles();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('xs'));
  const { company_cs_url, company_contact_email, company_phone_number } = shipment;

  const RE_PROTOCOL = /^http/;
  const protocolChecker = (url: string) => (RE_PROTOCOL.test(url) ? url : `//${url}`);

  return (
    <Dialog
      aria-labelledby="contact options"
      fullScreen={isNarrow}
      classes={{ paper: classes.paper }}
      open={open}
      onClose={onClose}
    >
      <DialogTitle data-testid="dialog-title">
        <FormattedMessage id="contact.title" />
        <IconButton aria-label={formatMessage({ id: 'contact.close' })} className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <List>
        {company_cs_url && (
          <ListItem
            button
            component="a"
            href={protocolChecker(company_cs_url)}
            rel="noopener"
            className={classes.listItem}
            data-testid="button-cs"
            alignItems="flex-start"
          >
            <ListItemIcon>
              <ComputerIcon />
            </ListItemIcon>
            <ListItemText
              primary={formatMessage({ id: 'contact.website' })}
              secondary={company_cs_url}
            />
          </ListItem>
        )}
        {company_contact_email && (
          <ListItem
            button
            component="a"
            href={`mailto:${company_contact_email}`}
            rel="noopener noreferrer"
            className={classes.listItem}
            data-testid="button-email"
            alignItems="flex-start"
          >
            <ListItemIcon>
              <EmailIcon />
            </ListItemIcon>
            <ListItemText
              primary={formatMessage({ id: 'contact.email' })}
              secondary={company_contact_email}
            />
          </ListItem>
        )}
        {company_phone_number && (
          <ListItem
            button
            component="a"
            href={`tel:${company_phone_number}`}
            rel="noopener noreferrer"
            className={classes.listItem}
            data-testid="button-phone"
            alignItems="flex-start"
          >
            <ListItemIcon>
              <PhoneIcon />
            </ListItemIcon>
            <ListItemText
              primary={formatMessage({ id: 'contact.phone' })}
              secondary={company_phone_number}
            />
          </ListItem>
        )}
      </List>
    </Dialog>
  );
}
